import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Formik } from 'formik';

import { Modal, Button, DatePicker, Input, Tooltip } from '../../../components';
import { DateHelper, renderRecordValue } from '../../../utils';
import { timezonesDictionary } from '../../../dictionaries';
import { DEFAULT_TIME_FORMAT, TC_ENDRIDE_DEFAULT_VALUES } from '../../../constants/constants';
import { TCEndRideSchema } from '../../../utils/validations';
import styles from './index.module.css';
import { Alert } from 'antd';
import { deadHeadMileageTooltipMessage } from '../../MH_Admin/MHEditRide/TooltipMessages';
import { InfoIcon } from '../../../assets';
import { useDateTimeHandlers } from '../../MH_Admin/MHEditRide/useDateTimeHandlers';

export const RIDE_STATUS_MODALS_TYPES = {
  DRIVER_ARRIVED: 'DRIVER_ARRIVED',
  START_RIDE: 'START_RIDE',
  EDIT_RIDE: 'EDIT_RIDE',
  END_RIDE: 'END_RIDE',
};

const modalsInfo = {
  DRIVER_ARRIVED: {
    title: 'Driver Check-In Details',
  },
  START_RIDE: {
    title: 'Ride Started - Specify date and time',
  },
  EDIT_RIDE: {
    title: 'Edit Ride - Specify date and time',
  },
  END_RIDE: {
    title: 'Ride Ended',
    showComment: true,
  },
};

export const RideStatusModal = ({ visibleModal, onCancel, onConfirm, modalType, date, ride,
   setRideStatusModalType, tcConfiguration, estMileage }) => {

  const [pickUpDate, setPickUpDate] = useState('');
  const [extendedDateWindowAnchor, setExtendedDateWindowAnchor] = useState(null);
  const isEndRide = modalType === RIDE_STATUS_MODALS_TYPES.END_RIDE;

  useEffect(() => {
    if (!visibleModal) {
      setExtendedDateWindowAnchor(null);
      return;
    }

    const timezone = date?.timezone || ride?.pickUpDate?.timezone;
    if (timezone) {
      setExtendedDateWindowAnchor(DateHelper.utcToZonedTime(new Date(), timezone));
    }
  }, [visibleModal, date?.timezone, ride?.pickUpDate?.timezone]);

  const { disabledDateForDriverChekinBeforeAfter, disabledDateTime } = useDateTimeHandlers({
    ride,
    setFieldValue: () => {},
  });

  const minAvailableDateTime = useMemo(() => {
    const timezone = ride?.pickUpDate?.timezone;
    if (!timezone) return null;

    if (modalType === RIDE_STATUS_MODALS_TYPES.END_RIDE && ride?.startDate) {
      return DateHelper.utcToZonedTime(ride.startDate, timezone);
    }

    if (
      (modalType === RIDE_STATUS_MODALS_TYPES.START_RIDE ||
        modalType === RIDE_STATUS_MODALS_TYPES.EDIT_RIDE) &&
      ride?.driverArrivalTime
    ) {
      return DateHelper.utcToZonedTime(ride.driverArrivalTime, timezone);
    }

    return null;
  }, [modalType, ride?.startDate, ride?.driverArrivalTime, ride?.pickUpDate?.timezone]);

  useEffect(() => {
    const timezone = date?.timezone || ride?.pickUpDate?.timezone;
    if (date.datetime) {
      setPickUpDate(DateHelper.utcToZonedTime(date.datetime, timezone));
    } else if (minAvailableDateTime) {
      setPickUpDate(minAvailableDateTime);
    } else if (ride?.pickUpDate?.datetime) {
      setPickUpDate(DateHelper.utcToZonedTime(ride.pickUpDate.datetime, timezone));
    }

    return;
  }, [ride, date, minAvailableDateTime]);

  const disabledTimeForPickUpField = useCallback(
    (selectedDate) => {
      if (modalType === RIDE_STATUS_MODALS_TYPES.DRIVER_ARRIVED || !minAvailableDateTime) {
        return { disabledHours: () => [], disabledMinutes: () => [] };
      }

      return disabledDateTime(minAvailableDateTime, pickUpDate || selectedDate)(selectedDate);
    },
    [modalType, minAvailableDateTime, disabledDateTime, pickUpDate],
  );

const diabledDatesForPickUpField = useCallback(() => {
    if (modalType === RIDE_STATUS_MODALS_TYPES.DRIVER_ARRIVED) {
      return disabledDateForDriverChekinBeforeAfter;
    }

    if (!ride?.pickUpDate?.datetime || !ride?.pickUpDate?.timezone) return () => false;
    const timezone = ride.pickUpDate.timezone;
    const pickUpDateLocal = DateHelper.utcToZonedTime(ride.pickUpDate.datetime, timezone);
    const pickUpTime = new Date(pickUpDateLocal).getTime();

    let lowerBound;
    if (modalType === RIDE_STATUS_MODALS_TYPES.END_RIDE && ride?.startDate) {
      lowerBound = new Date(DateHelper.utcToZonedTime(ride.startDate, timezone)).getTime();
    } else if (
      (modalType === RIDE_STATUS_MODALS_TYPES.START_RIDE ||
        modalType === RIDE_STATUS_MODALS_TYPES.EDIT_RIDE) &&
      ride?.driverArrivalTime
    ) {
      lowerBound = new Date(DateHelper.utcToZonedTime(ride.driverArrivalTime, timezone)).getTime();
    } else if (ride?.driverArrivalTime) {
      lowerBound = new Date(DateHelper.utcToZonedTime(ride.driverArrivalTime, timezone)).getTime();
    } else {
      lowerBound = pickUpTime - (20 * 60 * 60 * 1000);
    }
    
    return (currentDate) => {
      if (!currentDate) return true;

      // Only the lower-bound calendar day is enabled
      // (START: driver check-in day; END: ride start day)
      const lowerDayStart = new Date(lowerBound);
      lowerDayStart.setHours(0, 0, 0, 0);

      const dayStart = new Date(currentDate);
      dayStart.setHours(0, 0, 0, 0);

      return dayStart.getTime() !== lowerDayStart.getTime();
    };
  }, [
    modalType,
    ride?.pickUpDate?.datetime,
    ride?.pickUpDate?.timezone,
    ride?.startDate,
    disabledDateForDriverChekinBeforeAfter,
    ride?.driverArrivalTime,
  ]);


  // const diabledTimeForPickUpField = useCallback(
  //   () => {


  //     if (modalType == RIDE_STATUS_MODALS_TYPES.DRIVER_ARRIVED) {

  //       return disabledDateForDriverChekinBeforeAfter;
  //     } else {


  //       return disabledDate(driverArrivalTime);
  //     }


  //   },
  //   [

  //     disabledDatesForPickUpDate,
  //     disabledDate,
  //     minAvailablePickUpDate,
  //   ]
  // );


  // const disabledDateTimeForPickUpField = useCallback(
  //   () =>
  //     disabledAllDateBeforeAfter({ date: pickUpDate, beforeDate: minAvailablePickUpDate, afterDate: maxAvailablePickUpDate }),
  //   [disabledAllDateBeforeAfter,
  //     pickUpDate,
  //     minAvailablePickUpDate,
  //   ]
  // );

  const handleChangeDateTime = ({ value, name, setFieldValue }) => {
    setPickUpDate(value);
    setFieldValue(name, DateHelper.zonedTimeToUtcISOS(value, date?.timezone));
  };

  const handleSelectDateTime = (selectedDate, setFieldValue) => {
    if (!selectedDate || !DateHelper.isValidDateTime(selectedDate)) return;
    handleChangeDateTime({ value: selectedDate, name: 'datetime', setFieldValue });
  };

  const handleCancel = async () => await onCancel();

  const handleDateTimeToCurrent =
    ({ setFieldValue, name }) =>
      () => {
        const currentDate = DateHelper.utcToZonedTime(new Date(), date?.timezone);
        setExtendedDateWindowAnchor(currentDate);
        setPickUpDate(currentDate);
        setFieldValue(
          name,
          DateHelper.zonedTimeToUtcISOS(currentDate, date?.timezone),
        );
      };

  const renderExtraFooter = () => null;

  // useEffect(() => () => { setRideStatusModalType(''); return; }, [setRideStatusModalType]);
  const isDisabled = (currentValues) => {
    if (isEndRide) {
      return !(currentValues?.datetime && !isNaN(currentValues?.actMileage) && (currentValues?.actMileage > 0))
    } else {
      return !(currentValues?.datetime)
    }

  }

  const hideWaitTimeInputForLegBOnSiteRoundTrip = useMemo(() => {
    if (!isEndRide) return false;
    if (!ride?.isRoundtrip || !ride?.driverRemainOnSite) return false;
    if (ride?.leg !== 'B') return false;
    const currentPickUp = new Date(ride?.pickUpDate?.datetime);
    const legAPickUp = new Date(ride?.connectedLeg?.pickUpDate?.datetime);
    if (Number.isNaN(+currentPickUp) || Number.isNaN(+legAPickUp)) return false;
    const hours = Math.abs(currentPickUp.getTime() - legAPickUp.getTime()) / (1000 * 60 * 60);
    return hours <= 24;
  }, [isEndRide, ride]);

  return (
    <Modal
      myClassName={classnames({
        [styles.rideStatusModal]: true,
        [styles.rideStatusEndedModal]: isEndRide,
      })}
      visible={visibleModal}
      onCancel={handleCancel}
      closable
      width={700}
      title={modalType === RIDE_STATUS_MODALS_TYPES.DRIVER_ARRIVED 
        ? `Driver Check-In Details in ${timezonesDictionary[date?.timezone]} timezone` :
        `${renderRecordValue(modalsInfo[modalType]?.title)} in ${timezonesDictionary[date?.timezone]} timezone`}>
      <div
        className={classnames({
          [styles.modalContent]: true,
        })}>
        <Formik
          initialValues={() => TC_ENDRIDE_DEFAULT_VALUES(isEndRide)}
          validationSchema={() => TCEndRideSchema(isEndRide, estMileage)}
        >
          {({ values, setFieldValue }) => {
            return (
              <form className={styles.rideStatusForm}>
                {isEndRide && (<div  >
                  <Alert message={'Mileage will only be added once'} description={'For update contact MedHaul admin'} type="info" showIcon banner
                    closable />

                  <div id="rideActualMileage" className={classnames({
                    [styles.mt_10]: true,
                    [styles.flex]: true,
                  })}  >
                    <label className={styles.comment}>
                      <span className={classnames({ [styles.required]: true, [styles.w135]: true, })}> Ride total mileage:</span>
                    </label>
                    <div className={styles.mt_10} >
                      <Input name='actMileage' type='number' precision={2}
                        step={0.01} />
                    </div>
                  </div>

                  {!hideWaitTimeInputForLegBOnSiteRoundTrip && (
                    <div className={styles.deadheadWrapper}>
                      <label className={styles.waitTimeLabel}>
                        <span className={styles.waitTimeTitle}>{"Total Wait Time:"}</span>
                      </label>

                      <div className={styles.timeInputFieldsContainer}>
                        <label className={styles.minsTimeControl}>
                          <label className={styles.timeInput}>
                            <Input name="waitTime" type='number' min={0} max={120} />
                            <span className={styles.measurement}> minute(s)</span>
                          </label>
                        </label>
                      </div>
                    </div>
                  )}


                  {tcConfiguration?.deadheadMileageConfigure &&

                    <div className={styles.deadheadWrapper} >
                      <label className={styles.deadhead}>
                        <span > Deadhead Mileage:</span>

                      </label>
                      <Input name='deadheadMileage' type='number' min={0} precision={2}
                        step={0.01} />
                      <span className={styles.tooltipWrapper}>
                        <Tooltip
                          title={
                            <span>
                              {deadHeadMileageTooltipMessage}
                            </span>
                          }>
                          <span className={styles.tooltipInfo}>
                            <InfoIcon width={30} height={30} className={styles.infoIcon} />
                          </span>
                        </Tooltip>
                      </span>
                    </div>
                  }
                </div>

                )}

                <div className={styles.datePickerWrapper} >
                  <label className={styles.datepickerLabel}>
                    <span className={styles.dateHeader}>Date</span>
                    <span className={styles.timeHeader}>Time</span>
                  </label>
                  <DatePicker
                    hideInputField
                    defaultOpen={visibleModal}
                    name='datetime'
                    onChange={handleChangeDateTime}
                    onSelect={(selectedDate) => handleSelectDateTime(selectedDate, setFieldValue)}
                    format={DEFAULT_TIME_FORMAT}
                    value={pickUpDate}
                    disabledDate={diabledDatesForPickUpField()}
                    disabledTime={disabledTimeForPickUpField}
                    // disabled={pickUpDateFieldDisabled}
                    showTime
                    use12Hours
                    renderExtraFooter={renderExtraFooter}
                    getPopupContainer={(trigger) => trigger.parentElement}
                  />
                </div>

                <div className={styles.setDateTime}>
                  <Button
                    myClassName={styles.dateTimeBtn}
                    onClick={handleDateTimeToCurrent({ name: 'datetime', setFieldValue })}>
                    Set date and time to current in {timezonesDictionary[date?.timezone]} timezone
                  </Button>
                </div>



                <div
                  className={classnames({
                    [styles.modalBtns]: true,
                    [styles.rideStatusBtn]: true,
                  })}>
                  <div>
                    <Button myClassName={styles.navigationBtn} onClick={handleCancel}>
                      Cancel
                    </Button>
                  </div>
                  <div>
                    <Button myClassName={styles.navigationBtn} onClick={onConfirm(values)} disabled={isDisabled(values)}>
                      Save
                    </Button>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};